/**
 * Organization Settings Page
 *
 * Manage organization details.
 *
 * @module pages/settings/OrganizationPage
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle, usePermissions } from '@/hooks';
import { db, functions } from '@/lib/firebase';
import { Card, CardHeader, Button, Input, Modal, Alert, useToast } from '@/components/ui';
import { Save, Copy, Check, AlertTriangle } from 'lucide-react';

// =============================================================================
// Component
// =============================================================================

export function OrganizationPage() {
  usePageTitle('Organization Settings');

  const navigate = useNavigate();
  const { organization, refreshOrganization, user } = useAuth();
  const { canUpdateOrgSettings } = usePermissions();
  const toast = useToast();

  const [name, setName] = useState(organization?.name || '');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  const hasChanges = name !== (organization?.name || '');

  // Check if user is the owner (can delete)
  const isOwner = organization?.ownerId === user?.id;
  const hasPaidSubscription = Boolean(
    organization?.stripeSubscriptionId && organization?.planId !== 'free'
  );

  const handleSave = async () => {
    if (!organization || !user) return;
    if (!name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    setSaving(true);
    try {
      const trimmedName = name.trim();

      await updateDoc(doc(db, 'organizations', organization.id), {
        name: trimmedName,
        updatedAt: serverTimestamp(),
      });

      // Update orgMemberships in user document with new org name
      // This keeps the denormalized data in sync for the org switcher
      const updatedMemberships = (user.orgMemberships || []).map((m) =>
        m.orgId === organization.id ? { ...m, orgName: trimmedName } : m
      );
      await updateDoc(doc(db, 'users', user.id), {
        orgMemberships: updatedMemberships,
      });

      // Refresh the org data in context
      await refreshOrganization();

      toast.success('Organization updated successfully');
    } catch (error) {
      console.error('Failed to update organization:', error);
      toast.error('Failed to update organization. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyId = async () => {
    if (!organization?.id) return;

    try {
      await navigator.clipboard.writeText(organization.id);
      setCopied(true);
      toast.success('Organization ID copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDeleteOrganization = async () => {
    if (!organization) return;

    setDeleting(true);
    try {
      const deleteOrg = httpsCallable(functions, 'deleteOrganization');
      await deleteOrg({
        organizationId: organization.id,
        confirmName: confirmName,
      });

      toast.success('Organization deleted successfully');
      setShowDeleteModal(false);

      // Navigate to home - the auth context will handle the missing org
      navigate('/');
    } catch (error: unknown) {
      console.error('Failed to delete organization:', error);
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to delete organization');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Organization Details */}
      <Card>
        <CardHeader
          title="Organization Details"
        />

        {!canUpdateOrgSettings && (
          <Alert variant="info" className="mb-4">
            Only organization owners and admins can edit organization settings.
          </Alert>
        )}

        <div className="space-y-4">
          <Input
            label="Organization Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp"
            disabled={!canUpdateOrgSettings}
          />

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges || !canUpdateOrgSettings}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="border-error-200">
        <CardHeader
          title="Danger Zone"
        />

        <div className="flex items-center justify-between p-4 bg-error-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">Delete Organization</p>
            <p className="text-sm text-gray-500">
              Permanently delete this organization and all of its data.
            </p>
          </div>
          <Button
            variant="danger"
            disabled={!isOwner || hasPaidSubscription}
            onClick={() => setShowDeleteModal(true)}
          >
            Delete
          </Button>
        </div>

        {!isOwner && (
          <Alert variant="info" className="mt-4">
            Only organization owners can delete the organization.
          </Alert>
        )}
        {hasPaidSubscription && isOwner && (
          <Alert variant="info" className="mt-4">
            Cancel your subscription before deleting the organization.
          </Alert>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setConfirmName('');
        }}
        title="Delete Organization"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setConfirmName('');
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteOrganization}
              loading={deleting}
              disabled={confirmName !== organization?.name}
            >
              Delete Organization
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-error-50 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-error-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-error-800">
              <p className="font-medium">This action cannot be undone.</p>
              <p className="mt-1">
                All organization data including API keys, team members, and billing
                history will be permanently deleted.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="font-mono font-semibold">{organization?.name}</span> to confirm
            </label>
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Organization name"
            />
          </div>
        </div>
      </Modal>

      {/* Discreet Organization ID */}
      <div className="text-center pt-4 text-xs text-gray-400">
        Organization ID: <code className="font-mono">{organization?.id}</code>
        <button
          onClick={handleCopyId}
          className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {copied ? <Check className="h-3 w-3 inline" /> : <Copy className="h-3 w-3 inline" />}
        </button>
      </div>
    </div>
  );
}